import React from "react";
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import PersonIcon from "@mui/icons-material/Person";
import HelpIcon from "@mui/icons-material/Help";
import LogoutIcon from "@mui/icons-material/Logout";
import { useTranslation } from 'react-i18next';
import { useUser } from "../contexts/UserContext";

interface UserMenuProps {
  showHelp?: boolean;
  helpUrl?: string;
  helpInNewTab?: boolean;
  onLogout: () => void;
}

const UserMenu: React.FC<UserMenuProps> = ({
  showHelp = true,
  helpUrl = "",
  helpInNewTab = true,
  onLogout
}) => {
  // State to control the dropdown menu anchor element
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  
  const { t } = useTranslation();
  const { getDisplayName, userProfile } = useUser();

  const accountSettingsLabel: string = t('accountSettings', 'Account & Settings');
  const helpLabel: string = t('help', 'Help');
  const signOutLabel: string = t('signOut', 'Sign out');

  // Open the menu by setting the anchor element to the button that was clicked
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // Close the menu by clearing the anchor element
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  // Define the menu items with label, icon, and click action
  const menuItems = [
    {
      label: accountSettingsLabel,
      icon: <PersonIcon />,
      onClick: () => {
        // Handle account settings navigation
        handleCloseMenu();
      },
      color: "inherit"
    },
    {
      label: helpLabel,
      icon: <HelpIcon />,
      onClick: () => {
        if (helpInNewTab) {
          window.open(helpUrl, "_blank");
        } else {
          window.location.href = helpUrl;
        }
        handleCloseMenu();
      },
      color: "inherit"
    },
    {
      label: signOutLabel,
      icon: <LogoutIcon sx={{ color: 'error.dark' }} />,
      onClick: () => {
        onLogout();
        handleCloseMenu();
      },
      color: "error.dark"
    },
  ];

  // Filter out the "Help" menu item if showHelp is false
  const effectiveMenuItems = showHelp
    ? menuItems
    : menuItems.filter((item) => item.label !== helpLabel);

  // Get user's full name and email
  const fullName = getDisplayName();
  const email = userProfile?.email || '';

  return (
    <>
      {/* Button that triggers the dropdown menu */}
      {
      <Typography variant="subtitle2">{t('welcome', 'Welcome') + ','}</Typography>
      }
      <Button
        onClick={handleOpenMenu}
        sx={{ color: "text.primary", textTransform: 'none' }}
        endIcon={<ArrowDropDownIcon sx={{ color: "text.primary" }} />}
      >
        <Typography variant="subtitle2">{fullName}</Typography>
        
      </Button>

      {/* Dropdown menu configuration */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            minWidth: '220px',
            maxWidth: '280px',
            boxShadow: '0px 2px 8px rgba(0,0,0,0.15)'
          }
        }}
      >
        {/* User info section */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            {fullName}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'normal'
            }}
          >
            {email}
          </Typography>
        </Box>
        <Divider />

        {/* Divider before the last item (Sign out) */}
        {effectiveMenuItems.length > 0 &&
          <Divider sx={{ mt: 1 }} />
        }
        
        {/* Menu items */}
        {effectiveMenuItems.map((item) => (
          <MenuItem
            key={item.label}
            onClick={item.onClick}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText
              primary={
                <Typography
                  color={item.color}
                  sx={{
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word'
                  }}
                >
                  {item.label}
                </Typography>
              }
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default UserMenu;
